import ToastConstant from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import queryClient from "../configs/react-query/query-client";
import queryKeys from "../configs/react-query/query-key";
import { PROGRESS_FOSSID_STATUS, VERIFICATION_TYPE } from "../constants/index";
import { processComponentConflictFlags } from "../utils/common";
import { useFetchData } from "./useFetchData";
import { useMutationData } from "./useMutationData";
import { FORCE_COMPLETE_VERIFICATION_ACTION } from "@constants/action";
import useSuccessToast from "@configs/react-toastify/snackbar";
import { getExternalApiErrorMessage } from "@utils/error";

export const useClarityAirVerification = (
  projectSeq,
  clarityAirVerificationSeq,
  modifiedComponentData = [],
  clarityAirScanFollowupResults = [],
) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [verificationStatus, setVerificationStatus] = useState("ON_ANALYSIS");
  const [approvalFlag, setApprovalFlag] = useState("N");
  const [projectData, setProjectData] = useState({});
  const [verificationData, setVerificationData] = useState({});
  const [currentActionData, setCurrentActionData] = useState({});
  const [verificationReportsData, setVerificationReportsData] = useState([]);
  const [verificationComment, setVerificationComment] = useState("");
  const [verificationFile, setVerificationFile] = useState(null);
  const [approvalComment, setApprovalComment] = useState("");
  const fulfillmentInfoRef = useRef(null);

  const { data: fetchedProjectData, isLoading: projectDataLoading, isFetching: projectDataFetching } = useFetchData(
    [queryKeys.getProjectByProjectId, projectSeq],
    `/project/${projectSeq}`,
    { method: "GET" },
    {},
    { enabled: !!projectSeq },
  );

  const { data: fetchedVerificationData, error: fetchedVerificationDataError, isLoading: verificationDataLoading, isFetching: verificationDataFetching } = useFetchData(
    [queryKeys.getClarityAirById, clarityAirVerificationSeq],
    `/${projectSeq}/${queryKeys.getClarityAirById}/${clarityAirVerificationSeq}`,
    { method: "GET" },
    {},
    { enabled: !!clarityAirVerificationSeq },
  );

  useEffect(() => {
    if (fetchedVerificationDataError) {
      if (fetchedVerificationDataError?.response?.status === 404) {
        navigate("/404", { state: { backTo: "/projects" } });
      }
    }
  }, [fetchedVerificationDataError]);

  const updateApprovalFlagMutation = useMutationData(
    "/clarity_air_verification_approval_flag",
    { method: "PUT" },
    {
      onSuccess: () => {
        setApprovalFlag("Y");
      },
      onError: (error) => {
        console.error("Error updating approval flag:", error);
      },
    },
  );

  const updateVerificationStatusToReviewMutation = useMutationData(
    "/clarity_air_verification_status?preStatus=",
    { method: "PUT" },
    {
      onSuccess: () => {
        setVerificationStatus(PROGRESS_FOSSID_STATUS.REVIEW);
        queryClient.invalidateQueries({
          queryKey: [queryKeys.getClarityAirScanComponentBySeq, clarityAirVerificationSeq],
        });
        queryClient.invalidateQueries({
          queryKey: [queryKeys.getClarityAirById, clarityAirVerificationSeq],
        });
        queryClient.invalidateQueries({
          queryKey: [queryKeys.getProjectByProjectId, projectSeq],
        });
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "CLARITYAIR" } });
      },
      onError: (error) => {
        console.error("Error updating verification status:", error);
      },
    },
  );

  const rejectVerificationMutation = useMutationData(
    (data) => {
      const { preStatus } = data;
      return `/clarity_air_verification_status${preStatus ? `?preStatus=${preStatus}` : ""}`;
    },
    { method: "PUT" },
    {
      onSuccess: (response, variables) => {
        const { verificationStatusCd } = variables;
        setVerificationStatus(verificationStatusCd);
        setApprovalFlag("N");
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "CLARITYAIR" } });
      },
      onError: (error) => {
        console.error("Error rejecting verification:", error);
      },
    },
  );

  const updateVerificationToFulfillmentMutation = useMutationData(
    "/clarity_air_verification_status",
    {
      method: "PUT",
      params: { preStatus: PROGRESS_FOSSID_STATUS.REVIEW },
    },
    {
      onSuccess: () => {
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "CLARITYAIR" } });
      },
    },
  );

  const decompleteFromDoneMutation = useMutationData(
    "/clarity_air_verification_status",
    {
      method: "PUT",
      params: { preStatus: PROGRESS_FOSSID_STATUS.DONE },
    },
    {
      onSuccess: () => {
        setVerificationStatus(PROGRESS_FOSSID_STATUS.CHECK_FULFILLMENT);
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "CLARITYAIR" } });
      },
      onError: (error) => {
        console.error("Error decompleting verification:", error);
      },
    },
  );

  const completeVerification = useMutationData(
    "/clarity_air_verification_status",
    {
      method: "PUT",
      params: { preStatus: "" },
    },
    {
      onSuccess: () => {
        setVerificationStatus(PROGRESS_FOSSID_STATUS.DONE);
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "CLARITYAIR" } });
      },
      onError: (error) => {
        console.error("Error completing verification:", error);
      },
    },
  );

  const moveToDoneMutation = useMutationData(
    "/clarity_air_verification_status",
    {
      method: "PUT",
      params: {
        preStatus: "",
        action: FORCE_COMPLETE_VERIFICATION_ACTION,
      },
    },
    {
      onSuccess: () => {
        setVerificationStatus(PROGRESS_FOSSID_STATUS.DONE);
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "CLARITYAIR" } });
      },
      onError: (error) => {
        console.error("Error moving to done:", error);
      },
    },
  );

  const updateApprovalRequestFlagMutation = useMutationData(
    "/clarity_air_verification_approval_request_flag",
    { method: "PUT" },
    {
      onSuccess: () => {
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "CLARITYAIR" } });
      },
      onError: (error) => {
        console.error("Error updating approval request flag:", error);
        const errorMessage = getExternalApiErrorMessage(error, t);
        useSuccessToast(TOAST.error, t("common.error"), errorMessage);
      },
    },
  );

  const fileGroupNo = fetchedProjectData?.fileGroupNo;
  const licensePolicySeq = fetchedProjectData?.licensePolicySeq;
  const verificationVersion = fetchedVerificationData?.verificationVersion;
  const projectId = fetchedVerificationData?.projectId;

  const { data: fetchedLicensePolicy } = useFetchData(
    [queryKeys.getLicensePolicyByLicensePolicySeq, licensePolicySeq],
    `/license_policy/${licensePolicySeq}`,
    { method: "GET" },
    {},
    { enabled: !!licensePolicySeq },
  );

  const { data: fetchedSourceCodeScanComponent, isLoading: scanComponentLoading, isFetching: scanComponentFetching } = useFetchData(
    [queryKeys.getClarityAirScanComponentBySeq, projectId, verificationVersion],
    `/${queryKeys.getClarityAirScanComponentBySeq}`,
    {
      method: "GET",
      params: {
        projectId,
        verificationVersion,
      },
    },
    {},
    { enabled: !!projectId && !!verificationVersion },
  );

  const {
    data: fetchedSourceCodeScanFollowupPlan,
    dataUpdatedAt: sourceCodeScanFollowupPlanUpdatedAt,
  } = useFetchData(
    [queryKeys.getClarityAirScanFollowupPlanBySeq, projectId, verificationVersion],
    `/${queryKeys.getClarityAirScanFollowupPlanBySeq}`,
    {
      method: "GET",
      params: {
        projectId,
        verificationVersion,
      },
    },
    {},
    {
      enabled: !!projectId && !!verificationVersion,
      refetchOnMount: true,
      staleTime: 0,
    },
  );

  const fetchedVerificationReportForPopup = useMutationData(
    "/verification_report_for_popup",
    { method: "POST" },
    {
      onSuccess: (response) => {
        if (response && Array.isArray(response)) {
          setVerificationReportsData(response);
        }
      },
      onError: (error) => {
        console.error("Error getting verification report for popup:", error);
      },
    },
  );

  const { data: fetchedSourceCodeAction } = useFetchData(
    [queryKeys.getClarityAirActionBySeq, clarityAirVerificationSeq],
    `/${queryKeys.getClarityAirActionBySeq}`,
    {
      method: "GET",
      params: { verificationSeq: clarityAirVerificationSeq },
    },
    {},
    { enabled: !!clarityAirVerificationSeq },
  );

  const moveToFulfillmentMutation = useMutationData(
    "/clarity_air_verification_followup_results",
    { method: "PUT" },
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error("Error moving to fulfillment:", error);
      },
    },
  );

  const moveToRequestMutation = useMutationData(
    "/clarity_air_verification_status",
    {
      method: "PUT",
      params: { preStatus: "" },
    },
    {
      onSuccess: () => {
        setVerificationStatus(PROGRESS_FOSSID_STATUS.REQUEST);
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "CLARITYAIR" } });
      },
      onError: (error) => {
        console.error("Error moving to request:", error);
      },
    },
  );

  const moveToVerificationMutation = useMutationData(
    "/clarity_air_verification_status",
    {
      method: "PUT",
      params: { preStatus: "" },
    },
    {
      onSuccess: () => {
        setVerificationStatus(PROGRESS_FOSSID_STATUS.VERIFICATION);
        setApprovalFlag("Y");
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "CLARITYAIR" } });
      },
      onError: (error) => {
        console.error("Error moving to verification:", error);
      },
    },
  );

  const moveToUsageTypeReviewMutation = useMutationData(
    "/clarity_air_verification_status",
    {
      method: "PUT",
      params: { preStatus: "" },
    },
    {
      onSuccess: () => {
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "CLARITYAIR" } });
      },
      onError: (error) => {
        console.error("Error moving to usage type review:", error);
      },
    },
  );

  const deCompleteFulfillmentMutation = useMutationData(
    "/clarity_air_verification_status",
    {
      method: "PUT",
      params: { preStatus: PROGRESS_FOSSID_STATUS.CHECK_FULFILLMENT },
    },
    {
      onSuccess: () => {
        setVerificationStatus(PROGRESS_FOSSID_STATUS.FULFILLMENT);
        navigate("/fulfillment-process");
      },
      onError: (error) => {
        console.error("Error decompleting fulfillment:", error);
      },
    },
  );

  const rejectReqFulfillmentMutation = useMutationData(
    "/clarity_air_verification_status",
    {
      method: "PUT",
      params: { preStatus: PROGRESS_FOSSID_STATUS.FULFILLMENT },
    },
    {
      onSuccess: () => {
        setVerificationStatus(PROGRESS_FOSSID_STATUS.REVIEW);
        navigate(`/projects/${projectSeq}/CLARITYAIR/${clarityAirVerificationSeq}`);
      },
      onError: (error) => {
        console.error("Error rejecting fulfillment request:", error);
      },
    },
  );

  const completeCheckFulfillmentMutation = useMutationData(
    "/clarity_air_verification_status",
    {
      method: "PUT",
      params: { preStatus: PROGRESS_FOSSID_STATUS.CHECK_FULFILLMENT },
    },
    {
      onSuccess: () => {
        setVerificationStatus(PROGRESS_FOSSID_STATUS.REVIEW);
        navigate(`/projects/${projectSeq}/CLARITYAIR/${clarityAirVerificationSeq}`);
      },
      onError: (error) => {
        console.error("Error completing fulfillment check:", error);
      },
    },
  );

  const updateVerificationStatusToReVerificationMutation = useMutationData(
    "/clarity_air_verification_status",
    {
      method: "PUT",
      params: { preStatus: "" },
    },
    {
      onSuccess: () => {
        setVerificationStatus(PROGRESS_FOSSID_STATUS.REVERIFICATION);
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "CLARITYAIR" } });
      },
      onError: (error) => {
        console.error("Error requesting re-verification:", error);
      },
    },
  );

  const reUploadVerificationFileMutation = useMutationData(
    queryKeys.clarityAirVerificationUpload,
    {
      method: "POST",
      headers: { "Content-Type": "multipart/form-data" },
    },
    {
      onSuccess: () => {
        ToastConstant(TOAST.success, "Re-upload successful");
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "CLARITYAIR" } });
      },
      onError: (error) => {
        const errorCode = extractErrorCode(error, "common.error-occurred");
        useSuccessToast(TOAST.error, "Error", errorCode);
      },
    },
  );

  const addVerificationReportMutation = useMutationData(
    "/verification_report",
    { method: "POST" },
    {
      onSuccess: () => {
        ToastConstant(TOAST.success, "Verification report added");
      },
      onError: (error) => {
        console.error("Error adding verification report:", error);
      },
    },
  );

  const uploadFulfillmentFileMutation = useMutationData(
    "/clarity_air_verification_upload_for_followup",
    { method: "POST", headers: { "Content-Type": "multipart/form-data" } },
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error("Error uploading fulfillment file:", error);
      },
    },
  );

  const moveToLoadIdentificationsMutation = useMutationData(
    "/clarity_air_verification_status",
    {
      method: "PUT",
      params: { preStatus: "" },
    },
    {
      onSuccess: () => {
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "CLARITYAIR" } });
      },
      onError: (error) => {
        console.error("Error moving to load identifications:", error);
      },
    },
  );

  const updateApprovalFlagForUsageTypeMutation = useMutationData(
    "/clarity_air_verification_approval_flag",
    { method: "PUT" },
    {
      onSuccess: () => {
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "CLARITYAIR" } });
      },
      onError: (error) => {
        console.error("Error updating approval flag for usage type:", error);
      },
    },
  );

  const rejectVerificationFromReviewMutation = useMutationData(
    "/clarity_air_verification_status",
    {
      method: "PUT",
      params: { preStatus: "REJECT_ON_REVIEW" },
    },
    {
      onSuccess: () => {
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "CLARITYAIR" } });
      },
      onError: (error) => {
        console.error("Error rejecting verification from review:", error);
      },
    },
  );

  const rejectVerificationFromOnVerificationMutation = useMutationData(
    "/clarity_air_verification_status",
    {
      method: "PUT",
      params: { preStatus: PROGRESS_FOSSID_STATUS.VERIFICATION },
    },
    {
      onSuccess: () => {
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "CLARITYAIR" } });
      },
      onError: (error) => {
        console.error("Error rejecting verification from on-verification:", error);
      },
    },
  );

  const updateScanComponentsMutation = useMutationData(
    `/${queryKeys.getClarityAirScanComponentBySeq}`,
    { method: "PUT" },
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error("Error updating scan components:", error);
      },
    },
  );

  const handleGetVerificationReportForPopup = () => {
    fetchedVerificationReportForPopup.mutate({
      projectId,
      verificationToolCd: VERIFICATION_TYPE.CLARITYAIR,
      verificationVersion,
      sortColumnName: "created_at",
      sortOrder: "desc",
    });
  };

  useEffect(() => {
    if (fetchedProjectData) {
      setProjectData(fetchedProjectData);
    }
  }, [fetchedProjectData]);

  useEffect(() => {
    if (fetchedVerificationData) {
      setVerificationData(fetchedVerificationData);
      setVerificationStatus(fetchedVerificationData.verificationStatusCd);
      setApprovalFlag(fetchedVerificationData.approvalFlag);
      setVerificationComment(fetchedVerificationData.verificationComment);
      setVerificationFile(fetchedVerificationData.verificationRealFileName);
      setApprovalComment(fetchedVerificationData.approvalComment || "");
    }
  }, [fetchedVerificationData]);

  const handleUpdateStatus = (newApprovalFlag, newStatus) => {
    if (newApprovalFlag === "Y") {
      updateApprovalFlagMutation.mutate({
        approvalFlag: "Y",
        clarityAirVerificationSeq,
        approvalComment,
      });
    }
    setApprovalFlag(newApprovalFlag);
    if (newStatus) {
      setVerificationStatus(newStatus);
    }
  };

  const handleMoveToReview = () => {
    let componentDataToSend = modifiedComponentData;
    if (!modifiedComponentData || modifiedComponentData.length === 0) {
      componentDataToSend = processComponentConflictFlags(fetchedSourceCodeScanComponent || [], fetchedLicensePolicy);
    } else {
      componentDataToSend = processComponentConflictFlags(modifiedComponentData, fetchedLicensePolicy);
    }

    return updateVerificationStatusToReviewMutation.mutateAsync({
      clarityAirVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.REVIEW,
      clarityAirScanComponents: componentDataToSend,
      clarityAirScanFollowupResults: [],
      verificationComment,
    });
  };

  const handleRejectVerification = (currentStep, preStatusCd) => {
    let verificationStatusCd;
    switch (preStatusCd) {
      case PROGRESS_FOSSID_STATUS.CHECK_FULFILLMENT:
        verificationStatusCd = PROGRESS_FOSSID_STATUS.FULFILLMENT;
        break;
      case PROGRESS_FOSSID_STATUS.FULFILLMENT:
        verificationStatusCd = PROGRESS_FOSSID_STATUS.REVIEW;
        break;
      case PROGRESS_FOSSID_STATUS.REVIEW:
        verificationStatusCd = PROGRESS_FOSSID_STATUS.VERIFICATION;
        break;
      default:
        verificationStatusCd = PROGRESS_FOSSID_STATUS.ANALYSIS_CHECK;
        break;
    }

    return rejectVerificationMutation.mutateAsync({
      clarityAirVerificationSeq,
      verificationStatusCd,
      preStatus: preStatusCd,
    });
  };

  const handleCompleteVerification = () => {
    const updatedFollowupResults = fulfillmentInfoRef.current
      ? fulfillmentInfoRef.current.getFulfillmentData()
      : clarityAirScanFollowupResults;

    return completeVerification.mutateAsync({
      clarityAirVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.DONE,
      verificationComment,
      clarityAirScanFollowupResults: updatedFollowupResults,
    });
  };

  const handleMoveToDone = () => {
    const updatedFollowupResults = fulfillmentInfoRef.current
      ? fulfillmentInfoRef.current.getFulfillmentData()
      : clarityAirScanFollowupResults;

    return moveToDoneMutation.mutateAsync({
      clarityAirVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.DONE,
      verificationComment,
      clarityAirScanFollowupResults: updatedFollowupResults,
    });
  };

  const handleMoveToFulfillment = async () => {
    const updatedFollowupResults = fulfillmentInfoRef.current
      ? fulfillmentInfoRef.current.getFulfillmentData()
      : clarityAirScanFollowupResults;

    let componentDataToSend = modifiedComponentData;
    if (!modifiedComponentData || modifiedComponentData.length === 0) {
      componentDataToSend = processComponentConflictFlags(fetchedSourceCodeScanComponent || [], fetchedLicensePolicy);
    } else {
      componentDataToSend = processComponentConflictFlags(modifiedComponentData, fetchedLicensePolicy);
    }

    try {
      await moveToFulfillmentMutation.mutateAsync({
        clarityAirVerificationSeq,
        clarityAirScanFollowupResults: updatedFollowupResults,
      });

      await updateVerificationToFulfillmentMutation.mutateAsync({
        clarityAirVerificationSeq,
        verificationStatusCd: PROGRESS_FOSSID_STATUS.FULFILLMENT,
        clarityAirScanFollowupResults: updatedFollowupResults,
        clarityAirScanComponents: componentDataToSend,
        verificationComment,
      });
    } catch (error) {
      console.error("Error in handleMoveToFulfillment:", error);
      throw error;
    }
  };

  const handleMoveToRequest = () => {
    return moveToRequestMutation.mutateAsync({
      clarityAirVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.REQUEST,
    });
  };

  const handleDecompleteFromDone = () => {
    return decompleteFromDoneMutation.mutateAsync({
      clarityAirVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.CHECK_FULFILLMENT,
    });
  };

  const handleToCompleteFulfillment = async () => {
    if (!clarityAirVerificationSeq) {
      alert("Error: Missing verification sequence. Please refresh the page and try again.");
      return;
    }

    const updatedFollowupResults = fulfillmentInfoRef.current
      ? fulfillmentInfoRef.current.getFulfillmentData()
      : clarityAirScanFollowupResults;

    const fulfillmentComment = fulfillmentInfoRef.current ? fulfillmentInfoRef.current.getFulfillmentComment() : "";
    const formData = fulfillmentInfoRef.current ? fulfillmentInfoRef.current.getFormDataForUpload() : new FormData();
    formData.append("clarityAirVerificationSeq", clarityAirVerificationSeq);

    let componentDataToSend = modifiedComponentData;
    if (!modifiedComponentData || modifiedComponentData.length === 0) {
      componentDataToSend = processComponentConflictFlags(fetchedSourceCodeScanComponent || [], fetchedLicensePolicy);
    } else {
      componentDataToSend = processComponentConflictFlags(modifiedComponentData, fetchedLicensePolicy);
    }

    try {
      await moveToFulfillmentMutation.mutateAsync({
        clarityAirVerificationSeq,
        clarityAirScanFollowupResults: updatedFollowupResults,
      });

      await updateVerificationToFulfillmentMutation.mutateAsync({
        clarityAirVerificationSeq,
        verificationStatusCd: PROGRESS_FOSSID_STATUS.CHECK_FULFILLMENT,
        clarityAirScanFollowupResults: updatedFollowupResults,
        clarityAirScanComponents: componentDataToSend,
        followupComment: fulfillmentComment,
      });

      try {
        await uploadFulfillmentFileMutation.mutateAsync(formData);
        setVerificationStatus(PROGRESS_FOSSID_STATUS.CHECK_FULFILLMENT);
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "CLARITYAIR" } });
      } catch (uploadError) {
        setVerificationStatus(PROGRESS_FOSSID_STATUS.CHECK_FULFILLMENT);
        alert("Fulfillment completed but file upload failed. You may need to upload the file manually.");
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "CLARITYAIR" } });
      }
    } catch (error) {
      alert("Failed to complete fulfillment. Please check your connection and try again.");
      throw error;
    }
  };

  const handleToFollowUpResult = () => {
    const updatedFollowupResults = fulfillmentInfoRef.current
      ? fulfillmentInfoRef.current.getFulfillmentData()
      : clarityAirScanFollowupResults;

    return moveToFulfillmentMutation.mutateAsync({
      clarityAirVerificationSeq,
      clarityAirScanFollowupResults: updatedFollowupResults,
    });
  };

  const handleToRequestReVerification = (dueDate) => {
    return updateVerificationStatusToReVerificationMutation.mutateAsync({
      clarityAirVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.REVERIFICATION,
      reverificationDueDt: dueDate ? dueDate.toISOString() : null,
    });
  };

  const handleSaveSourceCodeScanComponents = () => {
    const processedComponentData = processComponentConflictFlags(modifiedComponentData, fetchedLicensePolicy);
    updateScanComponentsMutation.mutate(processedComponentData);
  };

  const handleMoveToUsageTypeCheck = (approvalRequestFlag = "Y") => {
    return updateApprovalRequestFlagMutation.mutateAsync({
      clarityAirVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.VERIFICATION,
      approvalRequestFlag,
    });
  };

  const handleMoveToUsageTypeInput = (approvalFlag = "Y") => {
    return updateApprovalFlagForUsageTypeMutation.mutateAsync({
      clarityAirVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.VERIFICATION,
      approvalFlag,
    });
  };

  const handleRejectVerificationFromReview = () => {
    rejectVerificationFromReviewMutation.mutate({
      clarityAirVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.VERIFICATION,
    });
  };

  const handleRejectVerificationFromOnVerification = () => {
    rejectVerificationFromOnVerificationMutation.mutate({
      clarityAirVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.ANALYSIS_CHECK,
    });
  };

  const handleRejectUsageTypeCheck = () => {
    return updateApprovalRequestFlagMutation.mutateAsync({
      clarityAirVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.REQUEST,
      approvalRequestFlag: "N",
    });
  };

  const handleRejectUseTypeInput = () => {
    return updateApprovalFlagForUsageTypeMutation.mutateAsync({
      clarityAirVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.VERIFICATION,
      approvalFlag: "N",
    });
  };

  const handleRejectUseTypeReview = () => {
    moveToUsageTypeReviewMutation.mutate({
      clarityAirVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.VERIFICATION,
    });
  };

  const isLoading = projectDataLoading || verificationDataLoading || scanComponentLoading;
  const isFetching = projectDataFetching || verificationDataFetching || scanComponentFetching;

  const isReviewOrDoneStage =
    verificationStatus === PROGRESS_FOSSID_STATUS.REVIEW || verificationStatus === PROGRESS_FOSSID_STATUS.DONE;

  const isNotAnalysisOrCheckAnalysisOrRequest =
    verificationStatus !== PROGRESS_FOSSID_STATUS.ANALYSIS &&
    verificationStatus !== PROGRESS_FOSSID_STATUS.ANALYSIS_CHECK &&
    verificationStatus !== PROGRESS_FOSSID_STATUS.REQUEST;

  const isFulfillmentOrCheckFulfillmentStage =
    verificationStatus === PROGRESS_FOSSID_STATUS.FULFILLMENT ||
    verificationStatus === PROGRESS_FOSSID_STATUS.CHECK_FULFILLMENT;

  return {
    isLoading,
    isFetching,
    verificationStatus,
    approvalFlag,
    projectData,
    verificationData,
    projectId,
    verificationVersion,
    fetchedLicensePolicy,
    fetchedSourceCodeScanComponent,
    fetchedSourceCodeScanFollowupPlan,
    sourceCodeScanFollowupPlanUpdatedAt,
    fetchedSourceCodeAction,
    verificationReportsData,
    isReviewOrDoneStage,
    isNotAnalysisOrCheckAnalysisOrRequest,
    isFulfillmentOrCheckFulfillmentStage,
    fulfillmentInfoRef,
    handleUpdateStatus,
    handleMoveToReview,
    handleRejectVerification,
    handleMoveToDone,
    handleCompleteVerification,
    handleMoveToFulfillment,
    handleMoveToRequest,
    handleDecompleteFromDone,
    handleGetVerificationReportForPopup,
    handleToCompleteFulfillment,
    handleToRequestReVerification,
    handleMoveToUsageTypeCheck,
    handleMoveToUsageTypeInput,
    handleToFollowUpResult,
    handleRejectVerificationFromReview,
    handleRejectVerificationFromOnVerification,
    handleRejectUsageTypeCheck,
    handleRejectUseTypeInput,
    handleRejectUseTypeReview,
    handleSaveSourceCodeScanComponents,
  };
};
